import type { LoginVerdict } from './login-verdict.model';

/**
 * Internal QA: force a login BLOCK verdict for a test domain, so every block
 * screen is reachable from a dropdown instead of by arranging real state.
 *
 * **Why this exists.** The QA license overrides are deep on the FIRST gate and
 * empty everywhere downstream, so the verdicts past it each needed their own
 * contrivance: `INTEGRATION_BROKEN` meant pointing the delegate at a nonexistent
 * address, `DOMAIN_NOT_AUTHORIZED` meant a four-step console dance (promote a
 * domain, move the service account, detach the old one), and
 * `CONFIG_IN_PROGRESS` meant a second browser. That also left the four screens
 * whose copy product still has to review with no way to simply *look* at them.
 *
 * **The safety property is the list below.** It contains only verdicts that
 * BLOCK, so this lever can never mint a session — unlike the license override,
 * which can produce an APP-eligible tier. `APP` and `WIZARD` are absent by
 * construction and a tripwire test asserts they stay absent; adding either
 * would turn a screen-preview tool into an access-granting one.
 *
 * Written ONLY by the platform-admin QA console (test domains only, ~1h TTL);
 * `AuthenticationService.resolveLogin` only ever READS it, and only for an
 * allowlisted test domain, so a real customer's login never touches the key.
 */
export const FORCEABLE_BLOCK_VERDICTS = [
  'PROSPECT',
  'DOMAIN_NOT_AUTHORIZED',
  // TEMPORARY, closed beta only (EDUS-2994). Every test tenant already has a
  // customer row, so this lever is the only way QA can see the screen.
  'ONBOARDING_INVITE_ONLY',
  'NOT_LICENSED',
  'NEEDS_SUPER_ADMIN',
  'INTEGRATION_BROKEN',
  'CONFIG_IN_PROGRESS',
  'UNAVAILABLE',
] as const satisfies readonly LoginVerdict[];

export type ForceableBlockVerdict = (typeof FORCEABLE_BLOCK_VERDICTS)[number];

/**
 * Dropdown labels for the console. Each says what the tester will actually see,
 * because the verdict name alone doesn't distinguish (for instance
 * `NOT_LICENSED` renders two different screens depending on tier).
 */
export const FORCEABLE_BLOCK_VERDICT_LABELS: Record<
  ForceableBlockVerdict,
  string
> = {
  PROSPECT: "Prospect — “Let's Get You Started” + trial CTA",
  DOMAIN_NOT_AUTHORIZED: 'Unregistered Domain — known tenant, domain not on it',
  ONBOARDING_INVITE_ONLY:
    'Onboarding Invite-Only — licensed, not on the closed-beta allowlist',
  NOT_LICENSED: 'No Active License — upgrade copy (placeholder)',
  NEEDS_SUPER_ADMIN: 'Super Admin Required (placeholder)',
  INTEGRATION_BROKEN: 'Google Workspace Connection Needs Attention (placeholder)',
  CONFIG_IN_PROGRESS: 'Configuration in Progress — a colleague holds the lock',
  UNAVAILABLE: 'We Hit a Snag — transient (placeholder)',
};

/**
 * The forced verdict short-circuits BEFORE the licence read, so it needs no
 * Chargify state and works on any allowlisted test domain regardless of its
 * subscription. The trade-off is that `LoginContext` is empty, so copy driven
 * by it falls back:
 *
 * - `NOT_LICENSED` always renders the **no-SKU** screen, never the lapsed one
 *   (that branch reads `context.tier`). Use the license override's **Lapsed**
 *   scenario for the lapsed design — it reaches it through the real path.
 * - `CONFIG_IN_PROGRESS` names “another administrator” rather than a real
 *   colleague (no `onboardingLockedBy`).
 *
 * Both are noted on the console page so nobody reports them as bugs.
 */
export function qaVerdictOverrideKey(domain: string): string {
  return `qa_login_verdict_${domain}`;
}
